/**
 * 用户权益Hook
 * 为React组件提供用户权益数据和弹窗控制接口
 *
 * 注意：为避免与AuthGuard中的权益检查冲突，已禁用自动检查功能
 * 权益检查统一由AuthGuard管理，防止首次登录时多次调用API导致奖励立即消失
 */

import { useState, useCallback, useRef } from 'react';
import { userBenefitsManager } from '@/utils/userBenefitsManager';
import { BenefitItem } from '@/services/types';
import { getUserInfo } from '@/utils/auth';

/**
 * 判断当前页面是否在创作页面中
 * 创作页面包括：首页、文本创作、图片创作、视频创作
 */
export const isInCreationPages = (pathname: string): boolean => {
  const creationPages = ['/home', '/creation/text', '/creation/image', '/creation/video'];
  return creationPages.includes(pathname);
};

/**
 * 处理"去创作"按钮点击事件
 * @param currentPath 当前页面路径
 * @param onClose 关闭弹窗的回调函数
 */
export const handleGoToCreation = (currentPath: string, onClose: () => void): void => {
  onClose(); // 先关闭弹窗

  if (isInCreationPages(currentPath)) {
    // 如果在创作页面，只关闭弹窗，停留在当前页面
    return;
  } else {
    // 如果不在创作页面，在新标签页打开首页
    window.open('/home', '_blank');
  }
};

// Hook状态接口
interface UserBenefitsState {
  // 数据状态
  benefitsData: BenefitItem[] | null;
  isLoading: boolean;
  error: string | null;

  // 弹窗状态
  showWelcomeGift: boolean;
  showDailyReward: boolean;

  // 控制方法
  refreshBenefits: () => Promise<void>;
  closeWelcomeGift: () => void;
  closeDailyReward: () => void;

  // 调试信息
  debugInfo: object;
}

/**
 * 用户权益Hook选项
 */
interface UseUserBenefitsOptions {
  // 是否自动检查权益（默认true）
  autoCheck?: boolean;

  // 是否在组件挂载时立即检查（默认true）
  checkOnMount?: boolean;

  // 错误回调
  onError?: (error: Error) => void;

  // 弹窗显示回调
  onModalShow?: (type: 'welcome' | 'daily', data: BenefitItem[]) => void;
}

/**
 * 用户权益Hook
 */
export const useUserBenefits = (options: UseUserBenefitsOptions = {}): UserBenefitsState => {
  const { onError, onModalShow } = options;

  // 状态管理
  const [benefitsData, setBenefitsData] = useState<BenefitItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWelcomeGift, setShowWelcomeGift] = useState(false);
  const [showDailyReward, setShowDailyReward] = useState(false);
  const [debugInfo, setDebugInfo] = useState<object>({});

  // 防止重复检查的标记
  const isCheckingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  /**
   * 检查用户权益
   */
  const checkUserBenefits = useCallback(
    async (forceRefresh = false) => {
      if (isCheckingRef.current && !forceRefresh) {
        console.log('[useUserBenefits] 检查进行中，跳过重复检查');
        return;
      }

      try {
        isCheckingRef.current = true;
        setIsLoading(true);
        setError(null);

        console.log('[useUserBenefits] 开始检查用户权益');

        // 获取弹窗状态
        const modalState = await userBenefitsManager.checkModalState();

        // 更新状态
        setBenefitsData(modalState.benefitsData);
        setShowWelcomeGift(modalState.showWelcomeGift);
        setShowDailyReward(modalState.showDailyReward);

        // 更新调试信息
        setDebugInfo(userBenefitsManager.getDebugInfo());

        // 触发弹窗显示回调
        if (modalState.showWelcomeGift && modalState.benefitsData) {
          onModalShow?.('welcome', modalState.benefitsData);
        }
        if (modalState.showDailyReward && modalState.benefitsData) {
          onModalShow?.('daily', modalState.benefitsData);
        }

        console.log('[useUserBenefits] 权益检查完成', {
          showWelcomeGift: modalState.showWelcomeGift,
          showDailyReward: modalState.showDailyReward,
          benefitsCount: modalState.benefitsData?.length || 0,
        });

        hasCheckedRef.current = true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '检查用户权益失败';
        console.error('[useUserBenefits] 检查用户权益异常:', err);
        setError(errorMessage);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
      } finally {
        setIsLoading(false);
        isCheckingRef.current = false;
      }
    },
    [onError, onModalShow]
  );

  /**
   * 刷新权益数据
   */
  const refreshBenefits = useCallback(async () => {
    await checkUserBenefits(true);
  }, [checkUserBenefits]);

  /**
   * 关闭欢迎礼包弹窗
   * 注意：时间戳记录已在弹窗显示时完成，这里只需要更新UI状态
   */
  const closeWelcomeGift = useCallback(() => {
    setShowWelcomeGift(false);
    console.log('[useUserBenefits] 欢迎礼包弹窗已关闭');
  }, []);

  /**
   * 关闭每日奖励弹窗
   * 注意：时间戳记录已在弹窗显示时完成，这里只需要更新UI状态
   */
  const closeDailyReward = useCallback(() => {
    setShowDailyReward(false);
    console.log('[useUserBenefits] 每日奖励弹窗已关闭');
  }, []);

  // 组件挂载时检查权益 - 暂时禁用，避免与AuthGuard冲突
  // useEffect(() => {
  //   if (autoCheck && checkOnMount && !hasCheckedRef.current) {
  //     checkUserBenefits();
  //   }
  // }, [autoCheck, checkOnMount, checkUserBenefits]);

  // 监听localStorage变化（多标签页同步）- 暂时禁用，避免与AuthGuard冲突
  // useEffect(() => {
  //   const handleStorageChange = (event: StorageEvent) => {
  //     if (
  //       event.key === 'amai_welcome_gift_shown' ||
  //       event.key === 'amai_daily_reward_timestamp' ||
  //       event.key === 'amai_user_benefits_data'
  //     ) {
  //       console.log('[useUserBenefits] 检测到localStorage变化，更新状态');
  //       // 延迟更新，避免频繁触发
  //       setTimeout(() => {
  //         checkUserBenefits();
  //       }, 100);
  //     }
  //   };

  //   window.addEventListener('storage', handleStorageChange);
  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //   };
  // }, [checkUserBenefits]);

  // 页面可见性变化时检查权益（用户切换标签页回来时）- 暂时禁用，避免与AuthGuard冲突
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === 'visible' && autoCheck && hasCheckedRef.current) {
  //       console.log('[useUserBenefits] 页面重新可见，检查权益更新');
  //       checkUserBenefits();
  //     }
  //   };

  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, [autoCheck, checkUserBenefits]);

  return {
    // 数据状态
    benefitsData,
    isLoading,
    error,

    // 弹窗状态
    showWelcomeGift,
    showDailyReward,

    // 控制方法
    refreshBenefits,
    closeWelcomeGift,
    closeDailyReward,

    // 调试信息
    debugInfo,
  };
};

/**
 * 简化版Hook，仅用于检查是否需要显示弹窗
 */
export const useUserBenefitsCheck = () => {
  const [modalState, setModalState] = useState({
    showWelcomeGift: false,
    showDailyReward: false,
    showMonthlyReward: false,
    showPaymentReward: false,
    showPhoneVerificationReward: false,
    benefitsData: null as BenefitItem[] | null,
  });

  const checkBenefits = useCallback(async () => {
    try {
      // 增加判断手机号是否带*，如果带*，则不显示手机号验证奖励
      let phone = null;
      const userInfo = getUserInfo();
      if (userInfo && userInfo.mobile) {
        phone = userInfo.mobile;
      }
      if (phone && phone.includes('*')) {
        const state = {
            showWelcomeGift: false,
            showDailyReward: false,
            showMonthlyReward: false,
            showPaymentReward: false,
            showPhoneVerificationReward: false,
            benefitsData: null as BenefitItem[] | null,
        }
        setModalState({
          ...state,
        });
        return state;
      }
      const state = await userBenefitsManager.checkModalState();
      setModalState(state);
      return state;
    } catch (error) {
      console.error('[useUserBenefitsCheck] 检查权益失败:', error);
      return null;
    }
  }, []);

  const checkPaymentRewards = useCallback(async () => {
    try {
      const state = await userBenefitsManager.checkPaymentRewardState();
      setModalState(state);
      return state;
    } catch (error) {
      console.error('[useUserBenefitsCheck] 检查支付奖励失败:', error);
      return null;
    }
  }, []);

  const checkPhoneVerificationReward = useCallback(async () => {
    try {
      const state = await userBenefitsManager.checkModalState();
      // 修改状态，将手机号验证奖励设为true，其他设为false
      console.log('[useUserBenefitsCheck] 检查手机号验证奖励状态:', state);
      let showPhoneVerificationReward = false;
      if(state.showWelcomeGift){
        showPhoneVerificationReward = true;
      }
      setModalState({
        ...state,
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward,
      });
      return state;
    } catch (error) {
      console.error('[useUserBenefitsCheck] 检查手机号验证奖励失败:', error);
      return null;
    }
  }, []);

  // 手动关闭弹窗的方法
  const closeWelcomeGift = useCallback(() => {
    setModalState(prev => ({ ...prev, showWelcomeGift: false }));
    userBenefitsManager.markWelcomeGiftShown();
  }, []);

  const closeMonthlyReward = useCallback(() => {
    setModalState(prev => ({ ...prev, showMonthlyReward: false }));
    userBenefitsManager.updateMonthlyRewardTimestamp();
  }, []);

  const closePhoneVerificationReward = useCallback(() => {
    setModalState(prev => ({ ...prev, showPhoneVerificationReward: false }));
    // 标记手机号验证奖励已显示
    userBenefitsManager.markPhoneVerificationRewardShown();
  }, []);

  return {
    ...modalState,
    checkBenefits,
    checkPaymentRewards,
    checkPhoneVerificationReward,
    closeWelcomeGift,
    closeMonthlyReward,
    closePhoneVerificationReward,
    markWelcomeShown: userBenefitsManager.markWelcomeGiftShown.bind(userBenefitsManager),
    markDailyShown: userBenefitsManager.updateDailyRewardTimestamp.bind(userBenefitsManager),
    markMonthlyShown: userBenefitsManager.updateMonthlyRewardTimestamp.bind(userBenefitsManager),
    markPaymentShown: userBenefitsManager.updatePaymentRewardTimestamp.bind(userBenefitsManager),
  };
};

export default useUserBenefits;
